import { GraphQLEmailService } from './graphqlEmail';
import { isDemoMode } from './mockData';

export interface NominationSettings {
  receiverEmail: string;
  senderEmail?: string;
  ccEmail: string;
}

/**
 * Where a loaded value actually came from.
 *
 * This exists because "the receiver is empty" and "we do not know what the receiver is" are
 * different facts that used to arrive as the same object. loadSharedNominationSettings catches
 * every failure and falls back, so a total outage returned `{ receiverEmail: '', ccEmail: '' }`
 * — indistinguishable from a genuinely unconfigured organisation. A screen that then offered a
 * Save button wrote those empty strings over a real configuration.
 *
 *   remote      — EmailConfig answered. Authoritative, empty or not.
 *   cache       — the remote read failed and localStorage had a previous value.
 *   unavailable — the remote read failed and there was nothing cached. The settings returned
 *                 alongside this are DEFAULTS, not a configuration, and must not be saved.
 */
export type NominationSettingsSource = 'remote' | 'cache' | 'unavailable';

export interface LoadedNominationSettings {
  settings: NominationSettings;
  source: NominationSettingsSource;
}

const STORAGE_KEY = 'nomination_settings';
const SETTINGS_EVENT = 'nomination-settings-updated';
const SHARED_SETTINGS_LOAD_TIMEOUT_MS = 5000;
let sharedNominationSettingsLoads = 0;
let cachedSharedSettings: NominationSettings | null = null;
let inFlightSharedSettings: Promise<LoadedNominationSettings> | null = null;
/*
 * Where cachedSharedSettings came from.
 *
 * Cached alongside the value because a caller that must distinguish "configured as empty"
 * from "we could not find out" has to get the same answer from the memory cache as from a
 * fresh read. Storing only the settings made the second read look authoritative.
 */
let cachedSharedSource: NominationSettingsSource | null = null;

export const defaultNominationSettings: NominationSettings = {
  receiverEmail: '',
  ccEmail: '',
};

/**
 * The stored settings, or null when there are none to read.
 *
 * Split out from getNominationSettings so the difference between "localStorage held a value"
 * and "localStorage held nothing" survives. getNominationSettings flattens both to defaults,
 * which is the right contract for its callers and the wrong one for deciding whether a
 * configuration is known — see NominationSettingsSource.
 */
function readStoredNominationSettings(): NominationSettings | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    return {
      ...defaultNominationSettings,
      ...parsed,
      receiverEmail: parsed.receiverEmail || parsed.senderEmail || '',
    };
  } catch {
    return null;
  }
}

/** Unchanged contract: always a value, defaults when nothing is stored or it cannot be read. */
export function getNominationSettings(): NominationSettings {
  return readStoredNominationSettings() || defaultNominationSettings;
}

export function getCachedSharedNominationSettings(): NominationSettings | null {
  if (cachedSharedSettings) return cachedSharedSettings;
  try {
    if (!localStorage.getItem(STORAGE_KEY)) return null;
    cachedSharedSettings = getNominationSettings();
    return cachedSharedSettings;
  } catch {
    return null;
  }
}

function persistLocalNominationSettings(settings: NominationSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    receiverEmail: settings.receiverEmail,
    ccEmail: settings.ccEmail,
  }));
}

function normalizeNominationSettings(settings: Partial<NominationSettings> | null | undefined): NominationSettings {
  return {
    receiverEmail: settings?.receiverEmail?.trim() || settings?.senderEmail?.trim() || '',
    ccEmail: settings?.ccEmail?.trim() || '',
  };
}

function logNominationSettings(message: string, settings?: NominationSettings, extra?: unknown): void {
  console.info('[NominationSettings]', message, {
    receiverEmail: settings?.receiverEmail || '',
    ccEmail: settings?.ccEmail || '',
    extra,
  });
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

export function saveNominationSettings(settings: NominationSettings): void {
  persistLocalNominationSettings(settings);
  cachedSharedSettings = normalizeNominationSettings(settings);
  /* A value we just wrote is as authoritative as one we read back. */
  cachedSharedSource = 'remote';
  inFlightSharedSettings = null;
  window.dispatchEvent(new CustomEvent(SETTINGS_EVENT, { detail: settings }));
}

/**
 * Unchanged contract, for the callers that only need a value (useNominationRole and friends).
 *
 * Never rejects, and cannot tell you whether the value is real — which is exactly why anything
 * that WRITES must use loadSharedNominationSettingsWithSource instead.
 */
export async function loadSharedNominationSettings(options: { forceRefresh?: boolean } = {}): Promise<NominationSettings> {
  return (await loadSharedNominationSettingsWithSource(options)).settings;
}

/** The same read, reporting where the value came from. See NominationSettingsSource. */
export async function loadSharedNominationSettingsWithSource(options: { forceRefresh?: boolean } = {}): Promise<LoadedNominationSettings> {
  const loadId = ++sharedNominationSettingsLoads;
  const startedAt = performance.now();
  console.log('[Perf][NominationRole] loadSharedNominationSettings start', {
    loadId,
    isDemoMode: isDemoMode(),
    forceRefresh: Boolean(options.forceRefresh),
    hasCachedSettings: Boolean(cachedSharedSettings),
    hasInFlightLoad: Boolean(inFlightSharedSettings),
  });

  if (!options.forceRefresh && cachedSharedSettings && cachedSharedSource) {
    console.log('[Perf][NominationRole] loadSharedNominationSettings complete', {
      loadId,
      durationMs: Math.round(performance.now() - startedAt),
      source: 'memory cache',
      receiverEmail: cachedSharedSettings.receiverEmail,
      ccEmail: cachedSharedSettings.ccEmail,
    });
    return { settings: cachedSharedSettings, source: cachedSharedSource };
  }

  if (inFlightSharedSettings) {
    console.log('[Perf][NominationRole] loadSharedNominationSettings reusing in-flight promise', { loadId });
    return inFlightSharedSettings;
  }

  inFlightSharedSettings = loadSharedNominationSettingsFromSource(loadId, startedAt, Boolean(options.forceRefresh)).finally(() => {
    inFlightSharedSettings = null;
  });

  return inFlightSharedSettings;
}

async function loadSharedNominationSettingsFromSource(loadId: number, startedAt: number, forceRefresh: boolean): Promise<LoadedNominationSettings> {

  if (isDemoMode()) {
    const demoSettings = getNominationSettings();
    cachedSharedSettings = demoSettings;
    cachedSharedSource = 'remote';
    console.log('[Perf][NominationRole] loadSharedNominationSettings complete', {
      loadId,
      durationMs: Math.round(performance.now() - startedAt),
      source: 'demo/localStorage',
      receiverEmail: demoSettings.receiverEmail,
      ccEmail: demoSettings.ccEmail,
    });
    return { settings: demoSettings, source: 'remote' };
  }

  try {
    const apiStartedAt = performance.now();
    console.log('[Perf][NominationRole] GraphQLEmailService.getEmailConfig start', { loadId });
    const config = await withTimeout(
      GraphQLEmailService.getEmailConfig({ force: forceRefresh }),
      SHARED_SETTINGS_LOAD_TIMEOUT_MS,
      'GraphQLEmailService.getEmailConfig'
    );
    console.log('[Perf][NominationRole] GraphQLEmailService.getEmailConfig complete', {
      loadId,
      durationMs: Math.round(performance.now() - apiStartedAt),
      hasConfig: Boolean(config),
      hasNominationReceiverEmail: Boolean(config?.nominationReceiverEmail),
      hasNominationCcEmail: Boolean(config?.nominationCcEmail),
    });
    if (config) {
      const sharedSettings = normalizeNominationSettings({
        receiverEmail: config.nominationReceiverEmail,
        ccEmail: config.nominationCcEmail,
      });
      saveNominationSettings(sharedSettings);
      cachedSharedSettings = sharedSettings;
      cachedSharedSource = 'remote';
      logNominationSettings('Loaded shared EmailConfig source of truth', sharedSettings);
      console.log('[Perf][NominationRole] loadSharedNominationSettings complete', {
        loadId,
        durationMs: Math.round(performance.now() - startedAt),
        source: 'EmailConfig',
        receiverEmail: sharedSettings.receiverEmail,
        ccEmail: sharedSettings.ccEmail,
      });
      return { settings: sharedSettings, source: 'remote' };
    }
    logNominationSettings('No shared EmailConfig found; using local cached settings until email configuration is saved', getNominationSettings());
  } catch (error) {
    if (error instanceof Error && error.message.includes('timed out')) {
      console.warn('[Perf][NominationRole][Timeout]', {
        loadId,
        durationMs: Math.round(performance.now() - startedAt),
        timeoutMs: SHARED_SETTINGS_LOAD_TIMEOUT_MS,
        error: error.message,
      });
    }
    console.warn('[NominationSettings] Failed to load EmailConfig setting:', error);
  }

  /*
   * The remote read did not produce a configuration. What we return depends entirely on
   * whether anything was cached, and saying so is the whole point: an empty object with no
   * provenance is what let a failed load be saved back over a real value.
   */
  const stored = readStoredNominationSettings();
  const fallbackSettings = stored || defaultNominationSettings;
  cachedSharedSettings = fallbackSettings;
  cachedSharedSource = stored ? 'cache' : 'unavailable';
  console.log('[Perf][NominationRole][FallbackSettings]', {
    loadId,
    durationMs: Math.round(performance.now() - startedAt),
    receiverEmail: fallbackSettings.receiverEmail,
    ccEmail: fallbackSettings.ccEmail,
  });
  console.log('[Perf][NominationRole] loadSharedNominationSettings complete', {
    loadId,
    durationMs: Math.round(performance.now() - startedAt),
    source: 'localStorage fallback',
    receiverEmail: fallbackSettings.receiverEmail,
    ccEmail: fallbackSettings.ccEmail,
  });
  return { settings: fallbackSettings, source: cachedSharedSource };
}

/**
 * Raised when a save would blank a receiver or CC that is currently set, without the caller
 * having said it means to. See saveSharedNominationSettings.
 */
export const NOMINATION_CLEAR_NOT_CONFIRMED = 'Refusing to clear the nomination receiver or CC: the caller did not confirm this was intentional. This guards against a failed page load saving empty values over a real configuration.';

export interface SaveNominationSettingsOptions {
  /**
   * The caller has a CONFIRMED baseline and the administrator deliberately emptied a value.
   *
   * Required because clearing is a legitimate action — an organisation can decide to have no
   * nomination receiver — and cannot therefore be rejected outright. What can be rejected is a
   * caller that clears without having established what it is clearing, which is precisely what
   * a screen whose initial load failed would be doing.
   */
  allowClearing?: boolean;
}

/**
 * Which fields this save would blank, given what is stored now.
 *
 * Compared against the CURRENT stored value rather than against anything the caller passed,
 * so the check still holds when the caller’s idea of the baseline is itself wrong — the case
 * where a page loaded empty defaults after a failed fetch and believes them to be real.
 */
function clearedFields(next: NominationSettings, current: { nominationReceiverEmail?: string | null; nominationCcEmail?: string | null }): string[] {
  const cleared: string[] = [];
  if (!next.receiverEmail && (current.nominationReceiverEmail || '').trim()) cleared.push('receiver');
  if (!next.ccEmail && (current.nominationCcEmail || '').trim()) cleared.push('CC');
  return cleared;
}

/**
 * Persist to EmailConfig, which is the source of truth.
 *
 * ── The read-before-write guard ───────────────────────────────────────────────────────────
 *
 * This function already fetches the live config to preserve the SMTP fields it does not own,
 * so it costs nothing to also ask what the nomination fields currently hold. If this save
 * would blank one that is set, and the caller has not passed allowClearing, it refuses.
 *
 * That is the last line of defence rather than the first. The Nomination Settings screen also
 * refuses to save before it has initialised, and disables the button — but a guard that lives
 * in the UI protects only the UI as it is written today. This one holds for every caller, and
 * for the same screen after somebody edits it.
 */
export async function saveSharedNominationSettings(
  settings: NominationSettings,
  options: SaveNominationSettingsOptions = {}
): Promise<void> {
  const normalizedSettings = normalizeNominationSettings(settings);
  if (isDemoMode()) {
    /* Same guard in demo mode, against localStorage — the only store demo mode has. */
    const stored = readStoredNominationSettings();
    if (stored && !options.allowClearing) {
      const cleared = clearedFields(normalizedSettings, {
        nominationReceiverEmail: stored.receiverEmail,
        nominationCcEmail: stored.ccEmail,
      });
      if (cleared.length > 0) throw new Error(NOMINATION_CLEAR_NOT_CONFIRMED);
    }
    saveNominationSettings(normalizedSettings);
    return;
  }

  try {
    const config = await GraphQLEmailService.getEmailConfig({ force: true });
    if (!config) {
      throw new Error('Email configuration must be saved before nomination settings can be persisted.');
    }

    if (!options.allowClearing) {
      const cleared = clearedFields(normalizedSettings, config);
      if (cleared.length > 0) {
        logNominationSettings(
          `Refused a save that would clear the nomination ${cleared.join(' and ')}`,
          normalizedSettings
        );
        throw new Error(NOMINATION_CLEAR_NOT_CONFIRMED);
      }
    }

    let smtpPassword = config.smtpPassword || '';
    try {
      smtpPassword = atob(smtpPassword);
    } catch {
      // Use as-is when the backend already returns a decoded value.
    }

    await GraphQLEmailService.saveEmailConfig({
      smtpHost: config.smtpHost,
      smtpPort: config.smtpPort,
      smtpUsername: config.smtpUsername,
      smtpPassword,
      encryption: config.encryption,
      senderEmail: config.senderEmail,
      senderName: config.senderName,
      enabled: config.enabled,
      timesheetCcEmail: config.timesheetCcEmail || '',
      nominationReceiverEmail: normalizedSettings.receiverEmail,
      nominationCcEmail: normalizedSettings.ccEmail,
    });
    saveNominationSettings(normalizedSettings);
    logNominationSettings('Saved shared EmailConfig source of truth', normalizedSettings);
  } catch (error) {
    console.warn('[NominationSettings] Failed to save nomination settings in EmailConfig:', error);
    throw error;
  }
}

export function onNominationSettingsChange(callback: (settings: NominationSettings) => void): () => void {
  const listener = (event: Event) => {
    callback((event as CustomEvent<NominationSettings>).detail || getNominationSettings());
  };
  const storageListener = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) callback(getNominationSettings());
  };
  window.addEventListener(SETTINGS_EVENT, listener);
  window.addEventListener('storage', storageListener);
  return () => {
    window.removeEventListener(SETTINGS_EVENT, listener);
    window.removeEventListener('storage', storageListener);
  };
}

export function mergeNominationRecipients(
  primaryEmails: string[],
  options: { includeReceiver?: boolean; includeSender?: boolean; includeCc?: boolean } = {}
): string[] {
  const settings = getNominationSettings();
  const emails = [
    ...primaryEmails,
    (options.includeReceiver || options.includeSender) ? settings.receiverEmail : '',
    options.includeCc ? settings.ccEmail : '',
  ]
    .map(email => email.trim())
    .filter(Boolean);

  return Array.from(new Set(emails.map(email => email.toLowerCase())));
}
